'use strict';

import moment from "moment";
import { action, configure, flow, observable, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {guid} from "@uw-it-sis/lib-react/lib/Utils";
import {States, DaysOfWeek} from "@uw-it-sis/lib-react/lib/AppConstants";

import {convert24hour} from "./ScheduleBuilderUtils";
import api from "../lib/Api";

class ReservedTimeFilterStore {
    state = States.initial;
    showReservedTimeFilterModal = false;
    formData = this.defaultFormData();

    constructor() {
        makeObservable(this, {
            state: observable,
            showReservedTimeFilterModal: observable,
            formData: observable,
            updateFormData: action,
            closeReserveTimeFilterModal: action,
            openReserveTimeFilterModal: action
        });

        this.url = 'api/schedule/reservedTimes';
        this.termId = null;
        this.reservedTimeFilters = [];
        this.httpClient = appStore.getHttpClient();
    }

    defaultFormData() {
        let tempFilter = {
            startTime: "6:30 AM",
            endTime: "10:30 PM",
        };

        DaysOfWeek.map(day => {
            tempFilter[day.key] = false;
        });

        return tempFilter;
    }

    // Fetch list of reserve time filters
    loadReservedTimeFilters = flow(function* (termId) {
        this.state = States.pending;

        // Set termId if null or current termId is not the same as termId arg
        if (this.termId === null || this.termId !== termId) {
            this.termId = termId;
        }

        // for closing the modal if open (upon success of CRUD actions)
        this.showReservedTimeFilterModal = false;
        // Make sure the form data is reset after CRUD completion
        this.formData = this.defaultFormData();

        try {
            this.reservedTimeFilters = yield api.getReservedTimeFilters(termId)

            if (appStore.isDevMode()) {
                console.log(`ReservedTimeFilterStore: Data load complete for ${termId}`);
            }

            this.state = States.done;
        } catch(error) {
            this.state = States.error;
        }
    }.bind(this));

    // Action to add a new reserve time filter
    addReservedTimeFilter = flow(function* () {
        this.state = States.pending;

        let postData = this.formData;
        // termId is required along with form data
        postData.termId = this.termId;

        try {
            yield this.httpClient.doPost(`${this.url}`, postData);

            // Load list of filter with new data and close modal
            this.loadReservedTimeFilters(this.termId);
        } catch(error) {
            this.state = States.error;
        }
    }.bind(this));

    // Action to update an existing reserve time filter
    updateReservedTimeFilter = flow(function* () {
        this.state = States.pending;

        let putData = this.formData;
        // termId is required along with form data
        putData.termId = this.termId;

        try {
            yield this.httpClient.doPut(`${this.url}/${this.formData.id}`, putData);

            // Load list of filter with new data and close modal
            this.loadReservedTimeFilters(this.termId);
        } catch(error) {
            this.state = States.error;
        }
    }.bind(this));

    // Action to remove an existing reserve time filter
    deleteReservedTimeFilter = flow(function* (id) {
        this.state = States.pending;

        try {
            yield this.httpClient.doDelete(`${this.url}/${id}`);

            // Load list of filter with new data
            this.loadReservedTimeFilters(this.termId);
        } catch(error) {
            this.state = States.error;
        }
    }.bind(this));

    // Update the form data on change of form inputs
    updateFormData(property, value) {
        this.formData[property] = value;
    }

    // Update the state of the reserve time filter modal to close
    closeReserveTimeFilterModal() {
        this.showReservedTimeFilterModal = false;
        this.formData = this.defaultFormData();
    }

    // Update the state of the reserve time filter modal to open
    openReserveTimeFilterModal() {
        this.showReservedTimeFilterModal = true;
    }

    // Get list of reserve time filters for display
    getReservedTimeFilters() {
        return this.reservedTimeFilters;
    }

    // Checks if loading of the reserve time filter has been completed
    hasData() {
        return this.termId !== null;
    }

    // Creates an obj of the reserve time filters in the form of fullcalendar event objects
    getReservedTimeFiltersAsEvents(initialDate) {
        // If there are no reserved times, return false
        if (this.reservedTimeFilters && this.reservedTimeFilters.length > 0) {
            let events = this.reservedTimeFilters.map(filter => {
                return ({
                    title: "Reserved Time",
                    daysOfWeek: DaysOfWeek.filter(day => filter[day.key]).map(day => day.index),
                    startTime: convert24hour(filter.startTime),
                    endTime: convert24hour(filter.endTime),
                    startRecur: initialDate,
                    endRecur: moment(initialDate, 'YYYY-MM-DD').add(6, 'days').format('YYYY-MM-DD'),
                    extendedProps: {
                        reserved: true
                    }
                });
            });

            return ({
                id: guid(),
                events: events,
                classNames: ['scheduleBuilder-option--reserved', 'cursor-default'],
                name: "Reserved Time"
            });
        } else {
            return false;
        }
    }
}

export default new ReservedTimeFilterStore();